import React, {useState} from 'react';

import {Icon, Text} from '@albert-io/atomic';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import type {IconName} from '@fortawesome/fontawesome-common-types';
import {NewClassroomModal} from 'client/Classrooms/Teacher/NewClassroomModal/NewClassroomModal.react';
import {getUserQuery} from 'client/Clever/util';
import type {CleverUser} from 'client/Clever/util';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import NewFolderModal from 'components/PracticeView/FoldersModal/NewFolderModal/NewFolderModal.react';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

import {useTeacherClassrooms} from '../hooks/useTeacherClassrooms';

import {ActionCard} from './ActionCard';
import {AdaptiveSubjectsCallout} from './AdaptiveSubjectsCallout';
import {CleverImportCard} from './CleverImportCard';

// Per-card icon-chip colors. Need to update once sematic colors are available. meanwhile use data colors
const CHIP_GREEN = 'bg-[var(--color-data-green-100)] text-[var(--color-data-green-700)]';
const CHIP_PURPLE = 'bg-[var(--color-data-purple-100)] text-[var(--color-data-purple-700)]';
const CHIP_BLUE = 'background-brand-subtle content-brand';

export const HomeActions = () => {
  const {fetchClassrooms} = useTeacherClassrooms();
  const [isNewClassOpen, setIsNewClassOpen] = useState(false);
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);

  // Skip the Clever profile fetch while masquerading. Mirrors legacy ClassroomsList, which renders its no-sync variant when masquerading.
  const isMasquerading = masqueradeStore.isMasquerading();
  const {isReady} = useAreLatestQueriesReady(
    isMasquerading ? [] : [() => getUserQuery() as unknown as QueryBuilder]
  );
  const user =
    !isMasquerading && isReady ? (getUserQuery().getResource() as unknown as CleverUser) : null;
  const showClever = Boolean(user?.getMeta().isAllowCleverSync());

  const {isFeatureEnabled: isDiagnosticsEnabled} = useIsFeatureFlagEnabled('adaptive_diagnostics');

  return (
    <>
      <div className='flex flex-col gap-x4 rounded-x3 border border-border-primary background-primary p-x3'>
        <section aria-label='Classes' className='flex flex-col gap-x2'>
          <Text bold>Classes</Text>
          <div className='grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x2'>
            {showClever && user ? <CleverImportCard user={user} /> : null}
            <ActionCard
              data-testid='home-action__create-class'
              iconClassName={CHIP_GREEN}
              iconContent={
                <Icon
                  color='inherit'
                  icon={['fak', 'regular-chalkboard-circle-plus' as IconName]}
                  size='inherit'
                />
              }
              onClick={() => setIsNewClassOpen(true)}
              title='Create A Class'
            />
          </div>
        </section>

        <section aria-label='Classwork' className='flex flex-col gap-x2'>
          <Text bold>Classwork</Text>
          <AdaptiveSubjectsCallout />
          <div className='grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-x2'>
            <ActionCard
              data-testid='home-action__new-assignment'
              iconClassName={CHIP_PURPLE}
              iconContent={
                <Icon
                  color='inherit'
                  icon={['fak', 'regular-file-lines-circle-plus' as IconName]}
                  size='inherit'
                />
              }
              onClick={() => history.pushState(null, '/assignment-builder/create-assignment')}
              title='New Assignment'
            />
            {isDiagnosticsEnabled ? (
              <ActionCard
                data-testid='home-action__new-diagnostic'
                iconClassName={CHIP_PURPLE}
                iconContent={
                  <Icon
                    color='inherit'
                    icon={['fak', 'regular-file-lines-sparkle-bl' as IconName]}
                    size='inherit'
                  />
                }
                onClick={() => history.pushState(null, '/diagnostics')}
                title='New Adaptive Diagnostic'
              />
            ) : null}
            <ActionCard
              data-testid='home-action__new-folder'
              iconClassName={CHIP_BLUE}
              iconContent={
                <Icon
                  color='inherit'
                  icon={['fak', 'regular-folder-circle-plus' as IconName]}
                  size='inherit'
                />
              }
              onClick={() => setIsNewFolderOpen(true)}
              title='New Folder'
            />
          </div>
        </section>
      </div>

      {isNewClassOpen ? (
        <NewClassroomModal
          handleClose={() => setIsNewClassOpen(false)}
          onSubmit={fetchClassrooms}
        />
      ) : null}
      {isNewFolderOpen ? <NewFolderModal handleClose={() => setIsNewFolderOpen(false)} /> : null}
    </>
  );
};
